import type { DataProvider } from '@refinedev/core';
import { authFetch } from '../utils/api';
import { apiUrl } from '../utils/apiUrl';

/**
 * API 基礎路徑
 */
const API_BASE = "/api/admin";

/**
 * 資源名稱 → API 路徑映射
 * 未列出的資源會使用預設路徑：/api/admin/{resource}
 */
const RESOURCE_MAP: Record<string, string> = {
  users: "/api/admin/users",
  nodes: "/api/admin/nodes",
  tiers: "/api/admin/tiers",
  stats: "/api/admin/stats",
  // `logs` / `api-logs` resources retired in Sprint 26 W1-5 / E6 A1 —
  //   `vpn_api_log` table dropped.  See migration
  //   1714920102000-DropVpnApiLog and
  //   docs/architecture/observability-tables.md.
  "client-logs": "/api/admin/client-logs",
  "jwt-keys": "/api/admin/jwt-keys",
  "ota-updates": "/api/admin/ota-updates",
  "app-versions": "/api/admin/app-versions",
};

/**
 * 取得資源的 API 路徑
 */
function getResourceUrl(resource: string): string {
  return RESOURCE_MAP[resource] || `${API_BASE}/${resource}`;
}

/**
 * 解析後端回應 { code, msg, data }
 * code === 0 代表成功，否則拋出錯誤
 */
async function parseResponse<T = unknown>(response: Response): Promise<T> {
  const text = await response.text();
  let result: { code: number; msg: string; data: T };

  try {
    result = JSON.parse(text);
  } catch {
    throw new Error(`伺服器回傳非 JSON 格式 (${response.status})`);
  }

  if (result.code === 401) {
    const error: any = new Error(result.msg || "認證失敗");
    error.statusCode = 401;
    throw error;
  }

  if (result.code !== 0) {
    throw new Error(result.msg || "請求失敗");
  }

  return result.data;
}

/**
 * 自訂 Data Provider — 適配後端 { code, msg, data } 回應格式
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => apiUrl(API_BASE),

  /**
   * 取得列表（含分頁、排序、篩選）
   */
  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    // VPN-101：apiUrl 包住路徑，base 設定時是絕對 URL（第二參數被忽略），
    // 未設時與現行 new URL(path, origin) 等價。
    const url = new URL(
      apiUrl(getResourceUrl(resource)),
      window.location.origin,
    );

    // 分頁參數
    const { currentPage = 1, pageSize = 50 } = pagination ?? {};
    url.searchParams.set("page", String(currentPage));
    url.searchParams.set("pageSize", String(pageSize));

    // 排序
    if (sorters?.length) {
      url.searchParams.set(
        "sort",
        sorters.map((s) => `${s.field}:${s.order}`).join(","),
      );
    }

    // 篩選
    if (filters?.length) {
      for (const filter of filters) {
        if (
          "field" in filter &&
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        ) {
          url.searchParams.set(filter.field, String(filter.value));
        }
      }
    }

    // 額外的查詢參數（透過 meta 傳入）
    if (meta?.query) {
      for (const [key, value] of Object.entries(meta.query)) {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const response = await authFetch(url.toString());
    const data = await parseResponse<any>(response);

    // 後端可能回傳 { list, total } 或直接回傳陣列
    if (
      data &&
      typeof data === "object" &&
      ("list" in data || "items" in data)
    ) {
      const list = Array.isArray((data as any).list)
        ? (data as any).list
        : Array.isArray((data as any).items)
          ? (data as any).items
          : [];
      return {
        data: list,
        total: (data as any).total ?? list.length,
      };
    }

    // 直接回傳陣列的情況
    if (Array.isArray(data)) {
      return { data, total: data.length };
    }

    return { data: [], total: 0 };
  },

  /**
   * 取得單筆資料
   */
  getOne: async ({ resource, id }) => {
    const url = `${getResourceUrl(resource)}/${id}`;
    const response = await authFetch(url);
    const data = await parseResponse<any>(response);
    return { data };
  },

  /**
   * 新增資料
   */
  create: async ({ resource, variables }) => {
    const url = getResourceUrl(resource);
    const response = await authFetch(url, {
      method: "POST",
      body: JSON.stringify(variables),
    });
    const data = await parseResponse<any>(response);
    return { data };
  },

  /**
   * 更新資料
   */
  update: async ({ resource, id, variables }) => {
    const url = `${getResourceUrl(resource)}/${id}`;
    const response = await authFetch(url, {
      method: "PUT",
      body: JSON.stringify(variables),
    });
    const data = await parseResponse<any>(response);
    return { data };
  },

  /**
   * 刪除資料
   */
  deleteOne: async ({ resource, id }) => {
    const url = `${getResourceUrl(resource)}/${id}`;
    const response = await authFetch(url, {
      method: "DELETE",
    });
    const data = await parseResponse<any>(response);
    return { data };
  },

  /**
   * 自訂請求（用於非標準 CRUD 操作）
   */
  custom: async ({
    url,
    method = "GET",
    payload,
    headers: customHeaders,
    query,
  }) => {
    // VPN-101：同 getList，apiUrl 包住路徑供跨域部署。
    const fetchUrl = new URL(apiUrl(url), window.location.origin);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) {
          fetchUrl.searchParams.set(key, String(value));
        }
      }
    }

    const options: RequestInit = { method };
    if (payload) {
      options.body = JSON.stringify(payload);
    }
    if (customHeaders) {
      options.headers = customHeaders as Record<string, string>;
    }

    const response = await authFetch(fetchUrl.toString(), options);
    const data = await parseResponse<any>(response);
    return { data };
  },
};
